
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  FeedbackRepositoryInterface,
  TicketFeedbackInterface,
} from '@/services/QC-V2/feedback/interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { ScoreTypeInterface, ScoreTypeRepositoryInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface, ScoreGradeRepositoryInterface } from '@/services/QC-V2/score-grade/interfaces';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { FilterRequestData, ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import { TicketInterface } from '@/services/QC-V2/ticket/interfaces';
import FeedbackDetails from '@/components/qa/feedback/FeedbackDetails.vue';
import TicketDetails from '@/components/qa/ticket/TicketDetails.vue';
import TicketPrivateNotes from '@/components/qa/ticket/TicketPrivateNotes.vue';
import TicketReplies from '@/components/qa/ticket/TicketReplies.vue';
import TicketFeedbackComments from '@/components/qa/feedback/TicketFeedbackComments.vue';
import TicketFeedbackReview from '@/components/qa/feedback/TicketFeedbackReview.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    TicketFeedbackComments,
    TicketReplies,
    FeedbackDetails,
    TicketDetails,
    TicketPrivateNotes,
    TicketFeedbackReview,
  },
  inject: [
    'feedbackRepositoryV2',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
    'profileRepository',
  ],
})
export default class TicketFeedback extends Vue {
  private feedbackRepositoryV2!: FeedbackRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private scoreTypeRepositoryV2!: ScoreTypeRepositoryInterface;
  private scoreGradeRepositoryV2!: ScoreGradeRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;

  private feedback: TicketFeedbackInterface = {} as TicketFeedbackInterface;
  private ticket: TicketInterface = {} as TicketInterface;

  private scoreTypes!: ScoreTypeInterface[];
  private scoreGrades!: ScoreGradeInterface[];
  private tags!: TagInterface[];

  private scoreTypesMap: { [key: number]: string } = {};
  private scoreGradesMap: { [key: number]: string } = {};

  private loading: boolean = true;

  private profilesMap: ProfileMapInterface = {};

  private get windowHeight(): number {
    return window.screen.height - 300;
  }

  private userHasScopes(scope: string): boolean {
    return this.$store.getters.scopes.includes(scope);
  }

  private async created(): Promise<void> {
    if (!this.$route.params.id) {
      this.$errorDialog.show('Feedback id not provided');
      return;
    }

    try {
      await this.fetchScores();
      await this.fetchTags();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async initialize(filter?: FilterRequestData): Promise<void> {
    try {
      this.loading = true;
      const feedback = await this.feedbackRepositoryV2.getTicketFeedback(Number(this.$route.params.id));
      await this.fetchFeedbackUsersData(feedback);
      this.feedback = feedback;
      this.ticket = feedback.ticket;
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async fetchTags(): Promise<void> {
    if (!this.$store.getters.scopes.includes('qa:tag:read')) {
      return;
    }

    this.tags = await this.tagRepositoryV2.getActiveTags();
  }

  private async fetchScores(): Promise<void> {
    if (!this.$store.getters.scopes.includes('qa:score:type:read')) {
      return;
    }

    this.scoreTypes = await this.scoreTypeRepositoryV2.getAll();
    this.scoreGrades = await this.scoreGradeRepositoryV2.getAll();

    this.scoreTypes.forEach((scoreType) => {
      this.scoreTypesMap[scoreType.id] = scoreType.label;
    });

    this.scoreGrades.forEach((scoreGrade) => {
      this.scoreGradesMap[scoreGrade.id] = scoreGrade.label;
    });
  }

  private async fetchFeedbackUsersData(feedback: TicketFeedbackInterface): Promise<void> {
    const review = feedback.ticket.reviews.find((r) => r.reviewedId === feedback.feedbackTo);
    if (!review) {
      this.$errorDialog.show('Feedback not found for given user!');
      return;
    }

    const ids: string[] = [
      feedback.feedbackTo,
      feedback.feedbackFrom,
      review.reviewedId,
      review.reviewerId,
    ];

    ids.push(...review.comments.map((comment) => comment.author));

    feedback.ticket.replies.forEach((reply) => {
      if (!reply.comments.length) {
        return;
      }

      reply.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    feedback.comments.forEach((comment) => {
      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    if (feedback.ticket.notes && feedback.ticket.notes.length) {
      feedback.ticket.notes.forEach((note) => {
        if (!ids.includes(note.author)) {
          ids.push(note.author);
        }
      });
    }

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });

    const [firstName, lastName] = this.$store.getters.name.split(' ');
    this.profilesMap[this.$store.getters.id] = {
      userId: this.$store.getters.id,
      profileId: '',
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
    };
  }
}
